import axios, { AxiosError, AxiosRequestConfig } from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL as string;
export const API = `${BACKEND}/api`;

// Access token is held in memory only (never localStorage). Refresh token lives in
// an HttpOnly cookie; the readable csrf_token cookie guards refresh/logout.
let accessToken: string | null = null;
export const setAccessToken = (t: string | null): void => {
  accessToken = t;
};
export const getAccessToken = (): string | null => accessToken;

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

export function csrfHeader(): Record<string, string> {
  const csrf = readCookie("csrf_token");
  return csrf ? { "X-CSRF-Token": csrf } : {};
}

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${accessToken}` } as any;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true, headers: csrfHeader() })
      .then((r) => {
        setAccessToken(r.data.access_token);
        return r.data.access_token as string;
      })
      .catch(() => {
        setAccessToken(null);
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

// On a 401 for a normal API call, try a single silent refresh then retry.
api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = (error.config || {}) as AxiosRequestConfig & { _retry?: boolean };
    const url = original.url || "";
    if (error.response?.status === 401 && !original._retry && !url.includes("/auth/")) {
      original._retry = true;
      const t = await refreshAccessToken();
      if (t) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${t}` } as any;
        return api(original);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail: unknown): string {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e: any) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (typeof (detail as any).msg === "string") return (detail as any).msg;
  if (typeof (detail as any).message === "string") return (detail as any).message;
  return String(detail);
}

export default api;
